let store;
export const setStore = (s) => {
  store = s;
};
import { checkOnlineStatus, db, decryptObject, encryptObject, getCacheByKey, patchCacheItem } from "../../view/shared/utilities/cache"
import { t } from "@lingui/macro"
import { addRequestToQueue } from './queue';

function isCacheValid(timestamp) {
  const now = Date.now()
  const cacheDuration = 30 * 24 * 60 * 60 * 1000 // 30 days in milliseconds
  return now - timestamp < cacheDuration
}

async function unlockApp(pin, encryptedPin, salt) {
  const decryptedPin = decryptObject(encryptedPin, salt)
  return decryptedPin === pin;
}

export async function handleOfflineRequest(config, error) {
  // For login requests, try to authenticate against local credentials
  if (config.isLoginRequest) {
    const { username, password } = config;
    try {
      const userCredentials = await getCacheByKey("users", "userId", username);
      if (!userCredentials) {
        alert(t({ id: `offline_login_msg` }));
        return Promise.reject(new Error("Unable to login offline. No credentials stored."));
      }
      const unlocked = await unlockApp(password, userCredentials.encryptedPin, userCredentials.salt);
      if (unlocked) {
        const response = await getCacheByKey("cacheStore", "url", username);
        const userData = decryptObject(response.data.encrypted, response.data.salt);
        return {
          data: JSON.parse(userData).data,
          status: 200,
          headers: { "X-Cache": "HIT" },
        };
      } else {
        return Promise.reject(new Error("Incorrect username or PIN."));
      }
    } catch (e) {
      return Promise.reject(e);
    }
  }

  // For GET requests, try to serve from cache
  if (config.cacheEnabled) {
    const cachedResponse = await getCacheByKey("cacheStore", "url", config.url);
    if (cachedResponse && isCacheValid(cachedResponse.timestamp)) {
      return {
        data: cachedResponse.data,
        status: 200,
        headers: { "X-Cache": "HIT" },
      };
    }
    return Promise.reject(new Error(`No cached data available or cache is expired for ${config.url}`));
  }

  // For non-GET requests (mutations), queue them
  if (config.protocol === 'ws') {
    console.log(`[safeApiCall] Network error for WS request. Queuing request.`);
    const { optimisticActions, tempId } = config;
    if (optimisticActions && optimisticActions.length) {
      optimisticActions.forEach(action => store.dispatch(action));
    }
    // For WS, the entire config is the request object.
    // The 'body' for encryption is the payload.
    const requestToQueue = {
      ...config,
      body: config.payload,
      status: 'pending',
      attempts: 0,
    };
    await addRequestToQueue(requestToQueue, config.userId);
    return Promise.resolve({
      data: { offline: true, queued: true, tempId: tempId }
    });

  } else if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method)) {
    console.log(`[safeApiCall] Network error for ${config.method} ${config.url}. Queuing request.`);

    // The config object from the action creator should be rich with info
    const {
      url,
      method,
      data,
      headers,
      tempId,
      entityType,
      dependsOn,
      optimisticActions,
      successAction,
      failAction
    } = config;

    // 1. Dispatch the optimistic action immediately to update the UI
    if (optimisticActions.length) {
      optimisticActions.forEach(action => store.dispatch(action));
    }

    // 2. Add the request to the queue with all necessary metadata
    const requestToQueue = {
      url,
      method,
      body: data,
      headers,
      tempId,
      entityType,
      dependsOn: dependsOn || null, // ID of the parent request
      status: 'pending',
      attempts: 0,
      // Store action types to be used by the queue processor
      reduxActions: {
        success: successAction,
        fail: failAction
      }
    };
    await addRequestToQueue(requestToQueue, config.userId);

    // 3. Return a specific object to signal the caller that the request was queued.
    return Promise.resolve({
      data: {
        offline: true,
        queued: true,
        tempId: tempId
      }
    });
  }

  // If it's not a cacheable GET and not a mutation, reject.
  return Promise.reject(error);
}

export const patchEncryptedCacheItem = async (table, primaryKey, updateBody) => {
  try {
    const item = await getCacheByKey(table, 'cacheId', primaryKey);
    if (!item) {
      throw new Error(`No item found in ${table} with key ${primaryKey}`);
    }

    const decryptedData = decryptObject(item.encryptedBody, item.salt);
    const parsedData = JSON.parse(decryptedData);

    let updatedBody;

    if (item.protocol === 'ws') {
      // Logic for WS protocol (handles nested 'payload.data')
      updatedBody = {
        ...parsedData,
        payload: {
          ...parsedData.payload,
          data: {
            ...parsedData.payload.data,
            ...updateBody
          }
        }
      };
    } else {
      // Logic for non-WS (HTTP) protocols
      // Use a switch statement to handle different entity structures
      switch (item.entityType) {
        case 'meeting':
        case 'group':
        // Add other entity types with a flat structure here
        default:
          // Default logic for requests with a flat top-level body
          updatedBody = {
            ...parsedData,
            body: {
              ...parsedData.body,
              ...updateBody
            }
          };
          break;
      }
    }

    // Encrypt the updated body.
    const encryptedBody = encryptObject(JSON.stringify(updatedBody), item.salt);

    // Update the cache item, but only the encrypted body.
    await patchCacheItem(table, primaryKey, {
      ...item,
      encryptedBody: encryptedBody
    });
  } catch (error) {
    console.error(`Error patching encrypted cache item: ${error.message}`);
    throw error; // Re-throw the error to signal failure to the caller.
  }
};

export const addCachedMeetingTransaction = async (primaryKey, transaction) => {
  try {
    const item = await getCacheByKey("offlineRequests", 'cacheId', primaryKey);
    if (!item) {
      throw new Error(`No item found in meeting with key ${primaryKey}`);
    }
    const decryptedData = decryptObject(item.encryptedBody, item.salt);
    const parsedData = JSON.parse(decryptedData);
    const updaredData = { ...parsedData, transactions: [...(parsedData.transactions || []), transaction] };
    console.log("Updated data with new transaction:", updaredData);
    const encryptedData = encryptObject(JSON.stringify(updaredData), item.salt);
    await patchCacheItem("offlineRequests", primaryKey, {
      ...item,
      encryptedBody: encryptedData,
      payload: updaredData,
    });
  } catch (error) {
    console.error(`Error patching encrypted cache item: ${error.message}`);
  }
}

export const patchCachedRequestsHeaders = async () => {
  const { user } = store.getState()
  let offlineRequests = await db.offlineRequests.toArray();
  offlineRequests = offlineRequests.filter(({ userId }) => userId === user.id)
  const backendHeaders = { Authorization: `Bearer ${user?.token}` }
  const blockChainHeaders = { Authorization: `Bearer ${user?.tokenBC}` }
  const backendEntities = ['members', 'group', 'meeting']

  for (const req of offlineRequests) {
    try {
      const headers = backendEntities.includes(req.entityType) ? backendHeaders : blockChainHeaders;
      const decryptedString = decryptObject(req.encryptedBody, req.salt);
      let decryptedBody = JSON.parse(decryptedString);

      if (req.protocol === 'ws') {
        decryptedBody.payload.headers = headers;
      } else {
        decryptedBody.headers = headers;
      }

      const encryptedBody = encryptObject(JSON.stringify(decryptedBody), req.salt);
      await patchCacheItem('offlineRequests', req.cacheId, {
        encryptedBody: encryptedBody,
        status: 'pending',
        error: null,
      });

      if (req.entityType === 'members') {
        store.dispatch({
          type: 'UPDATE_MEMBER_STATUS',
          payload: { tempId: req.cacheId, syncStatus: 'pending' },
        });
      }
    } catch (error) {
      console.error(`Failed to patch headers for request ${req.cacheId}:`, error);
    }
  }
}

export async function safeApiCall(config, callback) {
  const isOnline = await checkOnlineStatus ();
  if (isOnline && !config.needBodyUpdate && !config.dependsOn) {
    if (config.protocol === 'ws') {
      store.dispatch(config);
      const { optimisticActions } = config;
      if (optimisticActions && optimisticActions.length) {
        optimisticActions.forEach(action => store.dispatch(action));
      }
      return Promise.resolve({
        data: {
          offline: false,
          queued: false,
        }
      });
    }
    try {
      const response = await callback(); // Try network first
      return response;
    } catch (error) {
      // Check if the error is a network error
      if (error.isAxiosError && !error.response) {
        console.warn('[safeApiCall] Network error detected, handling offline.', error);
        return handleOfflineRequest(config, error);
      }
      // For other types of errors (e.g., 4xx, 5xx), re-throw them
      throw error;
    }
  } else {
    // Explicitly offline, go straight to offline handling
    console.log('[safeApiCall] App is offline, handling request accordingly.');
    return handleOfflineRequest(config, new Error("Application is offline."));
  }
}
